
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useState, useEffect } from 'react';

interface NewsItem {
  title: string;
  source: string;
  pubDate: string;
  summary: string;
  originalLink: string;
  googleNewsLink: string;
  imageUrl?: string;
}

const WEBAPP_URL = "https://script.google.com/macros/s/AKfycbzfvVB4re5QJ0KzpfMeTf1ehiqy5bcWpIK0v4SpIHnsMfeUecIyHTZC4IEBJ57AHB9_Lg/exec";

export default function LatestNews() {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });
  const [newsItems, setNewsItems] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedNews, setSelectedNews] = useState<NewsItem | null>(null);

  useEffect(() => {
    loadNews();
  }, []);

  const loadNews = async () => {
    try {
      const res = await fetch(WEBAPP_URL, { cache: "no-store" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();
      const items = (data && data.items) ? data.items : [];

      if (items.length === 0) {
        setError("ニュースがまだありません");
      } else {
        setNewsItems(items.slice(0, 20));
      }
    } catch (e) {
      setError("ニュースの取得に失敗しました");
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <section id="news" ref={ref} className="py-16 px-5 bg-[#0a0a0a]">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-start justify-between mb-6">
            <div>
              <p className="text-[#39ff14] text-xs tracking-[0.2em] uppercase mb-2">
                LATEST UPDATES
              </p>
              <h2 className="text-white text-3xl font-black">ホイール最新ニュース</h2>
            </div>
          </div>

          {loading && (
            <div className="text-center py-12">
              <div className="inline-block w-8 h-8 border-2 border-[#00d4ff] border-t-transparent rounded-full animate-spin"></div>
              <p className="text-[#888888] mt-4">読み込み中...</p>
            </div>
          )}

          {error && (
            <div className="text-center py-12">
              <i className="ri-error-warning-line text-[#ff4444] text-4xl mb-4"></i>
              <p className="text-[#888888]">{error}</p>
            </div>
          )}

          {!loading && !error && (
            <div className="space-y-3">
              {newsItems.map((item, index) => (
                <motion.button
                  key={index}
                  onClick={() => setSelectedNews(item)}
                  initial={{ opacity: 0, x: -30 }}
                  animate={inView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: index * 0.05 }}
                  className="w-full flex items-center gap-4 bg-[#1a1a1a] rounded-2xl p-4 hover:bg-[#00d4ff]/10 transition-all cursor-pointer"
                >
                  <div className="w-[240px] h-[160px] flex-shrink-0 rounded-xl overflow-hidden bg-[#2a2a2a]">
                    {item.imageUrl ? (
                      <img 
                        src={item.imageUrl} 
                        alt={item.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <i className="ri-newspaper-line text-[#00d4ff] text-3xl"></i>
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <span className="inline-block px-3 py-1 rounded-full text-[8px] font-bold mb-2 bg-[#39ff14] text-black">
                      NEWS
                    </span>
                    <h3 className="text-white text-base font-medium mb-2 line-clamp-2">
                      {item.title}
                    </h3>
                    <p className="text-[#666666] text-xs">
                      {item.source} / {item.pubDate}
                    </p>
                  </div>
                  <i className="ri-arrow-right-line text-[#666666] text-xl flex-shrink-0"></i>
                </motion.button>
              ))}
            </div>
          )}
        </motion.div>
      </section>

      {selectedNews && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-5"
          onClick={() => setSelectedNews(null)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[#1a1a1a] rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-[#1a1a1a] border-b border-[#333333] p-5 flex items-center justify-between">
              <span className="px-3 py-1 rounded-full text-[8px] font-bold bg-[#39ff14] text-black">
                NEWS
              </span>
              <button
                onClick={() => setSelectedNews(null)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#2a2a2a] transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-white text-xl"></i>
              </button>
            </div>

            <div className="p-6">
              <div className="w-full h-64 rounded-xl overflow-hidden bg-[#2a2a2a] mb-6">
                {selectedNews.imageUrl ? (
                  <img 
                    src={selectedNews.imageUrl} 
                    alt={selectedNews.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <i className="ri-newspaper-line text-[#00d4ff] text-6xl"></i>
                  </div>
                )}
              </div>

              <p className="text-[#666666] text-sm mb-3">
                {selectedNews.source} / {selectedNews.pubDate}
              </p>
              <h2 className="text-white text-2xl font-bold mb-6">{selectedNews.title}</h2>
              <p className="text-[#cccccc] text-base leading-relaxed mb-6">
                {selectedNews.summary}
              </p>
              
              <a
                href={selectedNews.originalLink || selectedNews.googleNewsLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#00d4ff] text-black font-bold rounded-xl hover:bg-[#00b8e6] transition-colors whitespace-nowrap cursor-pointer"
              >
                詳しくはこちら
                <i className="ri-external-link-line"></i>
              </a>
            </div>
          </motion.div>
        </div>
      )}
    </>
  );
}
